import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, Method } from "axios";
import storage from "@/shared/utils/storage/storage";
import { LOCALSTORAGE } from "@/constants/localstorage";
import { CommonApiResponseType } from "@/types/api";
import { commonErrorHandler } from "@/shared/utils/error/common.error.handler";
import { env } from "@/config/env";

const ERR_ACCESS_TOKEN_EXPIRED = "ERR_ACCESS_TOKEN_EXPIRED";

// Create an Axios instance with default configuration
const axiosInstance = axios.create({
    baseURL: env.baseUrl,
});

// Request interceptor to handle token authentication
axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            const accessTokenLocal = storage.get(localStorage, LOCALSTORAGE.accessToken);
            const accessTokenSession = storage.get(sessionStorage, LOCALSTORAGE.accessToken);
            const accessToken = accessTokenLocal || accessTokenSession;

            const refreshTokenLocal = storage.get(localStorage, LOCALSTORAGE.refreshToken);
            const refreshTokenSession = storage.get(sessionStorage, LOCALSTORAGE.refreshToken);
            const refreshToken = refreshTokenLocal || refreshTokenSession;

            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            } else if (refreshToken) {
                const newAccessToken = await fetchAccessToken(refreshToken as string);
                config.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            return config;
        } catch (error) {
            return Promise.reject(error);
        }
    },
    (error) => {
        commonErrorHandler(error);
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh on 401 errors
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const message = error.response?.data?.error;

        if (status === 401 && message === ERR_ACCESS_TOKEN_EXPIRED && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshTokenLocal = storage.get(localStorage, LOCALSTORAGE.refreshToken);
            const refreshTokenSession = storage.get(sessionStorage, LOCALSTORAGE.refreshToken);
            const refreshToken = refreshTokenLocal || refreshTokenSession;

            try {
                const newAccessToken = await fetchAccessToken(refreshToken as string);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                return axiosInstance(originalRequest);
            } catch (err) {
                commonErrorHandler(err);
                return Promise.reject(err);
            }
        }
        return Promise.reject(error);
    }
);

// Function to fetch a new access token using a refresh token
const fetchAccessToken = async (refreshToken?: string | undefined) => {
    try {
        const response = await axiosInstance.post("/auth/refresh-token/", { refreshToken });
        const newAccessToken = response.data.accessToken;
        // Save the new access token in both localStorage and sessionStorage
        storage.set(localStorage, LOCALSTORAGE.accessToken, newAccessToken);
        storage.set(sessionStorage, LOCALSTORAGE.accessToken, newAccessToken);
        return newAccessToken;
    } catch (error) {
        commonErrorHandler(error);
        throw error;
    }
};

export async function baseService<T>({ method, endPoint, data, headers, responseType, suppressErrorNotification }: {
    method: Method;
    endPoint: string;
    data?: any;
    headers?: AxiosRequestHeaders;
    responseType?: 'json' | 'blob' | 'arraybuffer';
    suppressErrorNotification?: boolean;
}): Promise<CommonApiResponseType<T>> {
    try {
        const reqHeaders = {
            'Content-Type': 'application/json',
            ...headers
        };
        const reqConfig: AxiosRequestConfig = {
            method,
            url: endPoint,
            data,
            headers: reqHeaders,
            responseType: responseType ?? 'json',
        };
        const response: AxiosResponse<CommonApiResponseType<T>> = await axiosInstance(reqConfig);
        if (responseType === 'arraybuffer') {
            const file = new Blob([response.data as any], { type: 'application/pdf' });
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'document.pdf';
            if (contentDisposition) {

                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '').trim();
                }
            }
            const fileUrl = URL.createObjectURL(file);
            
            // Download the file
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = filename;
            link.click();
            
            // Open file in new tab
            window.open(fileUrl, '_blank');
        }

        return response.data;
    } catch (error) {
        if (!suppressErrorNotification) {
            commonErrorHandler(error);
        }
        throw error;
    }
}
